import { Flex, theme } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { type Key, type ReactNode, useEffect, useMemo, useReducer, useState } from 'react';
import { catchError, EMPTY, finalize, of, switchMap, tap, throwError } from 'rxjs';

import DatePicker from '../../../components/controls/date-picker';
import FilterSelect from '../../../components/controls/filter-select';
import { DataTable, type DataTableProps } from '../../../components/data-table';
import { type CustomerWithSitesDto } from '../../../dto/customer/customer-with-sites-dto';
import ProblemDetailsDto from '../../../dto/error/problem-details-dto';
import { type ResponseDto } from '../../../dto/ResponseDto';
import { type SiteWithoutOrdersDto } from '../../../dto/site/site-without-orders-dto';
import { useNotification } from '../../../hooks/use-notification';
import useSecuredApiV2 from '../../../hooks/use-secured-api-v2';
import {
  ACTIVE_CUSTOMERS_WITH_SITES_END_POINT_URL,
  SITES_WITHOUT_ORDERS_END_POINT_URL,
} from '../../../utils/endpoints';
import { LocalDate } from '../../../utils/local-date';
import { formatSiteDisplayName, sortSitesByCountyAndName } from '../../../utils/utils';
import type Site from '../../../entities/site';

interface FiltersState {
  date?: LocalDate;
  customerId?: number | null;
  siteId?: number | null;
}

interface PageState {
  filters: FiltersState;
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const reducer = (
  state: PageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?: PageState['filters'] | PageState['pagination'] | PageState['sort'];
  }
): PageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload as FiltersState,
      };
    case 'SET_PAGINATION':
      return { ...state, pagination: action.payload as PageState['pagination'] };
    case 'SET_SORT':
      return { ...state, sort: action.payload as PageState['sort'] };
  }
};

const columns: DataTableProps<SiteWithoutOrdersDto>['columns'] = [
  {
    title: 'Customer name',
    dataIndex: ['customer', 'name'],
    key: 'customer-name',
  },
  {
    title: 'Site name',
    dataIndex: 'name',
    key: 'name',
    render: (value: string, site: SiteWithoutOrdersDto) => `${value} (${site.county})`,
  },
  {
    title: 'HHC site #',
    dataIndex: 'siteNumber',
    key: 'siteNumber',
    render: (value: string | undefined) => value,
  },
  {
    title: 'Customer site #',
    dataIndex: 'custSiteNumber',
    key: 'custSiteNumber',
    render: (value: string) => value,
  },
];
const SitesWithoutOrders = (): ReactNode => {
  const { token } = theme.useToken();
  const [state, setState] = useReducer(reducer, {
    filters: { date: new LocalDate() },
    pagination: { current: 1, pageSize: 10 },
    sort: {},
  });
  const api = useSecuredApiV2();
  const notification = useNotification();

  const [customers, setCustomers] = useState<CustomerWithSitesDto[]>([]);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [sitesWithoutOrders, setSitesWithoutOrders] = useState<SiteWithoutOrdersDto[]>([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);

  const { date, customerId, siteId } = state.filters;

  // Customers and their sites only feed the filters, so load them once
  useEffect(() => {
    const subscription = api
      .get(ACTIVE_CUSTOMERS_WITH_SITES_END_POINT_URL, {}, {})
      .pipe(
        switchMap((response) =>
          response.data
            ? of((response as ResponseDto<CustomerWithSitesDto[]>).data)
            : throwError(() => new ProblemDetailsDto(response))
        ),
        tap((customers) => {
          setCustomers(customers);
        }),
        catchError((error: ProblemDetailsDto) => {
          notification.error({
            message: ' ',
            description: error.detail ?? 'Failed to load customers',
          });
          return EMPTY;
        }),
        finalize(() => {
          setIsLoadingCustomers(false);
        })
      )
      .subscribe();
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!date) {
      setSitesWithoutOrders([]);
      return;
    }
    setIsLoadingSites(true);
    const subscription = api
      .get(
        SITES_WITHOUT_ORDERS_END_POINT_URL,
        {
          orderDate: date.toISOString(),
          customerId: customerId ?? undefined,
          // A site can only be picked once a customer is, so ignore a leftover one
          siteId: customerId ? siteId ?? undefined : undefined,
        },
        {}
      )
      .pipe(
        switchMap((response) =>
          response.data
            ? of((response as ResponseDto<SiteWithoutOrdersDto[]>).data)
            : throwError(() => new ProblemDetailsDto(response))
        ),
        tap((sites) => {
          setSitesWithoutOrders(sites);
        }),
        catchError((error: ProblemDetailsDto) => {
          // Clear the list, so sites loaded for previous filters aren't shown as results of the current ones
          setSitesWithoutOrders([]);
          notification.error({
            message: ' ',
            description: error.detail ?? 'Failed to load sites without orders',
          });
          return EMPTY;
        }),
        finalize(() => {
          setIsLoadingSites(false);
        })
      )
      .subscribe();
    // Ignore the response if the filters change or the component unmounts before it arrives
    return () => {
      subscription.unsubscribe();
    };
  }, [date?.toISOString(), customerId, siteId]);

  const tableProps = useMemo<Omit<DataTableProps<SiteWithoutOrdersDto>, 'columns'>>(
    () => ({
      title: 'Sites Without Orders',
      description: 'Sites that have not placed an order on the selected date',
      filtersNode: (
        <Flex gap={token.paddingXS}>
          <DatePicker
            value={state.filters.date ? dayjs(state.filters.date.toString()) : undefined}
            title="Order date"
            onChange={(value) => {
              setState({
                type: 'SET_FILTER',
                payload: { ...state.filters, date: new LocalDate(value) },
              });
              setState({
                type: 'SET_PAGINATION',
                payload: { ...state.pagination, current: 1 },
              });
            }}
          />
          <FilterSelect
            value={state.filters.customerId}
            mode="single"
            canSelectAll
            options={customers}
            optionValueProp="id"
            optionLabelProp="name"
            title="Customer"
            selectFirstOptionOnMount={false}
            onChange={(value) => {
              setState({
                type: 'SET_FILTER',
                payload: { ...state.filters, customerId: value ?? null, siteId: null },
              });
              setState({
                type: 'SET_PAGINATION',
                payload: { ...state.pagination, current: 1 },
              });
            }}
          />
          {state.filters?.customerId && (
            <FilterSelect
              value={state.filters?.siteId}
              mode="single"
              selectFirstOptionOnMount={false}
              options={sortSitesByCountyAndName(
                customers
                  .filter((c) => c.id === state.filters?.customerId)
                  .flatMap((c) => c.sites) as unknown as Site[]
              ).map((site) => ({
                value: site.id,
                label: formatSiteDisplayName(site),
              }))}
              title="Site"
              onChange={(value) => {
                setState({
                  type: 'SET_FILTER',
                  payload: { ...state.filters, siteId: value ?? null },
                });
              }}
            />
          )}
        </Flex>
      ),
      dataList: sitesWithoutOrders,
      onChange: setState,
    }),
    [sitesWithoutOrders, state.filters, customers]
  );

  return (
    <DataTable<SiteWithoutOrdersDto>
      {...tableProps}
      columns={columns}
      loading={isLoadingCustomers || isLoadingSites}
      currentPage={state.pagination.current}
      rowKey="id"
      size="small"
    />
  );
};

export default SitesWithoutOrders;
